import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { PlusIcon, PencilIcon, TrashIcon } from 'lucide-react';
import * as Icons from 'lucide-react';
import ConfirmDialog from '../components/ConfirmDialog';

const InvestorFoundationPointsPage = () => {
    const [points, setPoints] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingPoint, setEditingPoint] = useState(null);
    const [formData, setFormData] = useState({
        icon_name: 'Target',
        point_text: '',
        display_order: 0,
        is_active: true
    });
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [pointIdToDelete, setPointIdToDelete] = useState(null);

    const availableIcons = ['Target', 'BarChart3', 'Users', 'TrendingUp', 'Shield', 'CheckCircle', 'Star', 'Zap', 'Award', 'Heart'];

    useEffect(() => {
        fetchPoints();
    }, []);

    const fetchPoints = async () => {
        try {
            setLoading(true);
            const response = await axios.get('/api/admin/investor-foundation/points');
            if (response.data.success) {
                setPoints(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching points:', error);
            toast.error('Failed to load vision points');
        } finally {
            setLoading(false);
        }
    };

    const handleAddNew = () => {
        setEditingPoint(null);
        setFormData({
            icon_name: 'Target',
            point_text: '',
            display_order: points.length + 1,
            is_active: true
        });
        setIsModalOpen(true);
    };

    const handleEdit = (point) => {
        setEditingPoint(point);
        setFormData({
            icon_name: point.icon_name,
            point_text: point.point_text,
            display_order: point.display_order,
            is_active: point.is_active
        });
        setIsModalOpen(true);
    };

    const handleDelete = (id) => {
        setPointIdToDelete(id);
        setShowDeleteConfirm(true);
    };

    const confirmDelete = async () => {
        if (!pointIdToDelete) return;

        try {
            await axios.delete(`/api/admin/investor-foundation/points/${pointIdToDelete}`);
            toast.success('Vision point deleted successfully');
            fetchPoints();
        } catch (error) {
            console.error('Error deleting point:', error);
            toast.error('Failed to delete vision point');
        } finally {
            setShowDeleteConfirm(false);
            setPointIdToDelete(null);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        try {
            if (editingPoint) {
                await axios.put(`/api/admin/investor-foundation/points/${editingPoint.id}`, formData);
                toast.success('Vision point updated successfully');
            } else {
                await axios.post('/api/admin/investor-foundation/points', formData);
                toast.success('Vision point created successfully');
            }
            setIsModalOpen(false);
            fetchPoints();
        } catch (error) {
            console.error('Error saving point:', error);
            toast.error('Failed to save vision point');
        }
    };

    const renderIcon = (iconName) => {
        const IconComponent = Icons[iconName];
        return IconComponent ? <IconComponent size={20} /> : <Icons.Target size={20} />;
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-slate-800">Vision Points</h1>
                    <p className="text-slate-500 mt-1">Manage the key points in "Our Foundation" section</p>
                </div>
                <button
                    onClick={handleAddNew}
                    className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-semibold transition-all duration-200 shadow-lg hover:shadow-xl"
                >
                    <PlusIcon size={20} />
                    Add New Point
                </button>
            </div>

            {/* Points List */}
            {loading ? (
                <div className="flex items-center justify-center min-h-[400px]">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
                </div>
            ) : points.length === 0 ? (
                <div className="bg-white rounded-2xl p-12 text-center shadow-sm">
                    <p className="text-slate-500">No vision points yet. Add your first one!</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {points.map((point) => (
                        <div key={point.id} className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-md transition-shadow">
                            <div className="flex items-start justify-between mb-4">
                                <div className="p-3 bg-primary-50 rounded-xl text-primary-600">
                                    {renderIcon(point.icon_name)}
                                </div>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => handleEdit(point)}
                                        className="p-2 text-slate-600 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                                    >
                                        <PencilIcon size={18} />
                                    </button>
                                    <button
                                        onClick={() => handleDelete(point.id)}
                                        className="p-2 text-slate-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                    >
                                        <TrashIcon size={18} />
                                    </button>
                                </div>
                            </div>
                            <p className="text-slate-700 font-medium">{point.point_text}</p>
                            <div className="mt-4 flex items-center gap-3 text-sm text-slate-500">
                                <span>Order: {point.display_order}</span>
                                <span className={`px-2 py-1 rounded-full text-xs ${point.is_active ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}`}>
                                    {point.is_active ? 'Active' : 'Inactive'}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl max-w-md w-full p-6">
                        <h2 className="text-xl font-bold text-slate-800 mb-4">
                            {editingPoint ? 'Edit Vision Point' : 'Add Vision Point'}
                        </h2>
                        <form onSubmit={handleSubmit} className="space-y-4">
                            {/* Icon */}
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">Icon</label>
                                <select
                                    value={formData.icon_name}
                                    onChange={(e) => setFormData({ ...formData, icon_name: e.target.value })}
                                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
                                >
                                    {availableIcons.map(icon => (
                                        <option key={icon} value={icon}>{icon}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Point Text */}
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">Point Text</label>
                                <input
                                    type="text"
                                    value={formData.point_text}
                                    onChange={(e) => setFormData({ ...formData, point_text: e.target.value })}
                                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    placeholder="Purpose-led growth strategy"
                                    required
                                />
                            </div>

                            {/* Display Order */}
                            <div>
                                <label className="block text-sm font-semibold text-slate-700 mb-2">Display Order</label>
                                <input
                                    type="number"
                                    value={formData.display_order}
                                    onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) })}
                                    className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
                                    min="0"
                                />
                            </div>

                            {/* Active Toggle */}
                            <div className="flex items-center gap-3">
                                <input
                                    type="checkbox"
                                    id="is_active"
                                    checked={formData.is_active}
                                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                                    className="w-5 h-5 text-primary-600 rounded focus:ring-2 focus:ring-primary-500"
                                />
                                <label htmlFor="is_active" className="text-sm font-medium text-slate-700">
                                    Active
                                </label>
                            </div>

                            {/* Buttons */}
                            <div className="flex gap-3 pt-4">
                                <button
                                    type="button"
                                    onClick={() => setIsModalOpen(false)}
                                    className="flex-1 px-4 py-3 border border-slate-200 text-slate-700 rounded-xl font-semibold hover:bg-slate-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="flex-1 px-4 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-xl font-semibold transition-colors"
                                >
                                    {editingPoint ? 'Update' : 'Create'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Confirm Delete Dialog */}
            <ConfirmDialog
                isOpen={showDeleteConfirm}
                onClose={() => setShowDeleteConfirm(false)}
                onConfirm={confirmDelete}
                title="Delete Vision Point"
                message="Are you sure you want to delete this vision point? This action cannot be undone."
            />
        </div>
    );
};

export default InvestorFoundationPointsPage;
